/* eslint-disable no-param-reassign */
import { getConfig } from '@superbet-group/web.lib.config';
import { instanceGuard } from '@superbet-group/web.lib.utilities';

import OfferEvent from '@core/models/offer/OfferEvent';
import { PayoutService } from '../betSlip/PayoutService';
import SingletonError from '@core/services/common/errors/SingletonError';
import { getOddDisplayValue } from '@core/services/offer/helpers';
import Odd from '@core/models/offer/Odd';
import Category from '@models/struct/Category';
import Market from '@core/models/struct/Market';
import MarketGroupInfo from '@core/models/struct/MarketGroupInfo';
import OddType from '@models/struct/OddType';
import Sport from '@models/struct/Sport';
import Struct, { StructType } from '@models/struct/Struct';
import Tournament from '@models/struct/Tournament';

import { LiveBettingSpecifiersFormatter } from './_private/LiveBettingSpecifiersFormatter';
import { SuperOfferKeys } from '@core/models/offer/enums';
import { getIsNewBoostedOddDisplayEnabled } from '@app/shared/featureFlags/newBoostedOddDisplay';
import { simplifyTournamentName } from '@core/utils/sportsOffer/simplifyTournamentName';

let resolveStructPromise: (struct: Struct) => void;
const structReadyPromise = new Promise<Struct>((resolve) => {
    resolveStructPromise = resolve;
});
export class StructManager {
    private static instance: StructManager | undefined;
    private struct: Struct;
    private specifierFormatter: LiveBettingSpecifiersFormatter = new LiveBettingSpecifiersFormatter();

    private constructor() {
        if (StructManager.instance) {
            throw new SingletonError(this.constructor.name);
        }
    }

    public static getInstance(): StructManager {
        return instanceGuard(StructManager.instance)!;
    }

    public static createInstance(): StructManager {
        if (!StructManager.instance) {
            StructManager.instance = new StructManager();
        }
        return StructManager.instance;
    }

    public setStruct(struct: Struct): void {
        this.struct = struct;
        resolveStructPromise(struct);
    }

    public getStruct(): Struct {
        return this.struct;
    }

    public hasStruct(): boolean {
        return !!this.struct;
    }

    public async denormalizeEvent(event: OfferEvent): Promise<void> {
        await structReadyPromise;
        event.categoryName = this.getCategoryName(event.categoryId);
        event.sportName = this.getSportName(event.sportId);
        // Full name, as provided by Bookie. Unused in the UI, but retained for any purpose
        // that may require it later.
        event.tournamentOriginalName = this.getTournamentName(event.tournamentId);
        // This name is shown on event cards and used to resolve links,
        // but is not used in tournament headers and such
        event.tournamentName = simplifyTournamentName(event.tournamentOriginalName, event.categoryName);
        try {
            if (!event.sportOrder) {
                event.sportOrder = this.struct.sports[event.sportId].order!;
                event.tournamentOrder = this.struct.tournaments[event.tournamentId].order!;
                event.specialTournamentOrder = this.struct.tournaments[event.tournamentId].orderPromoted!;
            }
        } catch (e) {
            event.validStruct = false;
            return;
        }
        const oddsValid = this.denormalizeOdds(event);
        event.validStruct = oddsValid && !!event.sportName && !!event.categoryName && !!event.tournamentName;
    }

    public async denormalizeEvents(events: OfferEvent[]): Promise<void> {
        await Promise.all(events.map((event) => this.denormalizeEvent(event)));
    }

    /**
     * Returns true if fields in odd have all been denormalized successfully.
     */
    public denormalizeOdd(event: OfferEvent, odd: Odd): boolean {
        const { marketOriginalName, marketName } = this.getOddMarketNameWithFallback(odd, event);
        odd.marketOriginalName = marketOriginalName;
        odd.marketName = marketName;
        if (!odd.description) {
            odd.description = this.specifierFormatter.format(
                this.getOddDescription(odd.id),
                odd.specifiers!,
                event,
            );
        }
        odd.name = this.getOddNameWithFallback(odd, event);
        odd.isInPromotion = this.isInPromotion(odd);
        if (getConfig()?.superBonus?.isEnabled ?? false) {
            odd.isValidForSuperBonus = PayoutService.getInstance().isOddValidForSuperBonus(odd.tags);
        }

        const enrichedOddName = this.getEnrichedOddName(odd, event);
        odd.displayValue = getOddDisplayValue(enrichedOddName, odd.showSpecialBetStatus, odd.specialBetValue);

        return !!odd.marketOriginalName && !!odd.marketName && !!odd.name;
    }

    public getOddDescription(oddId: number): string {
        return this.getValueFromStruct(StructType.OddTypes, oddId, 'description');
    }

    private getOddMarketNameWithFallback(odd: Odd, event: OfferEvent): { marketOriginalName: string; marketName: string } {
        if (odd.initialMarketName) {
            return { marketOriginalName: odd.initialMarketName, marketName: odd.initialMarketName };
        } else {
            const marketOriginalName = this.getMarketName(odd.marketId);
            const marketName = this.specifierFormatter.format(
                marketOriginalName,
                odd.specifiers!,
                event,
            );
            return { marketOriginalName, marketName };
        }
    }

    private getOddNameWithFallback(odd: Odd, event: OfferEvent): string {
        if (odd.initialName) {
            return odd.initialName;
        } else {
            return this.specifierFormatter.format(
                this.getOddName(odd.id),
                odd.specifiers!,
                event,
            );
        }
    }

    private getEnrichedOddName(odd: Odd, event: OfferEvent) {
        if (this.isBetBoost(odd) && getIsNewBoostedOddDisplayEnabled()) {
            return this.specifierFormatter.format(
                this.getOddName(odd.id),
                odd.specifiers!,
                event,
            );
        } else {
            return odd.name;
        }
    }

    public denormalizeOdds(event: OfferEvent): boolean {
        let isValid = true;
        event.getOdds().forEach((odd: Odd) => {
            const result = this.denormalizeOdd(event, odd);
            if (!result) {
                isValid = false;
            }
        });
        return isValid;
    }

    public getSportName(id: number): string {
        return this.getValueFromStruct(StructType.Sports, id, 'name');
    }

    public getCategoryName(id: number): string {
        return this.getValueFromStruct(StructType.Categories, id, 'name');
    }

    public getMarketName(id: number): string {
        return this.getValueFromStruct(StructType.Markets, id, 'name');
    }

    public getTournamentName(id: number): string {
        return this.getValueFromStruct(StructType.Tournaments, id, 'name');
    }

    public getOddName(oddId: number): string {
        return this.getValueFromStruct(StructType.OddTypes, oddId, 'name');
    }

    public static clearInstance(): void {
        if (process.env.NODE_ENV !== 'test') {
            throw new Error('For use in tests only');
        }
        delete StructManager.instance;
    }

    public static isInitialized(): boolean {
        return StructManager.instance !== undefined;
    }

    private getValueFromStruct<T extends keyof Struct, ID extends keyof Struct[T]>(type: T, id: ID, prop: keyof Struct[T][ID], defaultValue = ''): string {
        if (!this.struct) {
            throw new Error('Struct manager not properly configured. Struct is missing.');
        }

        // After making id a mandatory propery, this if statement stayed as there are unit test that check for that.
        // TODO: check if there are use cases where AOT type checking get bypassed JIT. If so, move the check elsewhare. Otherwise remove this if statement, the related unit tests and these comments.
        if (id) {
            const entity = this.struct[type][id];

            // This check is needed even though TS does not warrent it. It is possible to target an id of a non existing entity.
            if (entity) {
                return String(entity[prop]);
            }
        }

        return defaultValue;
    }

    // eslint-disable-next-line class-methods-use-this
    private isInPromotion(odd: Odd): boolean {
        return !!odd.tags && Object.keys(SuperOfferKeys).some((tag) => odd.tags!.includes(tag));
    }

    // Temporary helper to allow us to recognise (and decorate) bet boost odds/markets and
    // enrich them in the UI. We should have an end-to-end solution for pricing and communicating
    // these in the fullness of time.
    // eslint-disable-next-line class-methods-use-this
    private isBetBoost(odd: Odd): boolean {
        return odd.specifiers?.was_price != null;
    }

    public setSports(sports: Record<number, Sport>): void {
        this.struct.sports = sports;
    }

    public setTournaments(tournaments: Record<number, Tournament>): void {
        this.struct.tournaments = tournaments;
    }

    public setCategories(categories: Record<number, Category>): void {
        this.struct.categories = categories;
    }

    public setMarkets(markets: Record<number, Market>): void {
        this.struct.markets = markets;
    }

    public setOddTypes(oddTypes: Record<number, OddType>): void {
        this.struct.oddTypes = oddTypes;
    }

    public setMarketGroupInfos(marketGroupInfos: Record<number, MarketGroupInfo>): void {
        this.struct.marketGroupInfos = marketGroupInfos;
    }
}

export interface IStructManager extends StructManager {}
